import { useQuery } from '@apollo/client';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { EmailStatusEnumType, ManageDealInvitesEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';

import statusEnumToReadable from 'utils/enums/status-enum-to-readable';

import SendInviteRemindersDialogButton, {
  MAX_REMINDERS_PER_INVITE,
  REMINDER_COOLDOWN_DAYS,
} from './SendInviteRemindersDialogButton';

const PAGE_SIZE = 50;

// GP-facing wording for how far the invite email got; opens are approximate (see headerNote below).
function getEmailActivityBadge(emailStatus: EmailStatusEnumType, joinedViaLink: boolean) {
  // Invite-link signups are never emailed, so "not emailed" would read like a fault for them.
  if (joinedViaLink && emailStatus === EmailStatusEnumType.Unknown) {
    return { label: 'Joined via link', color: 'sky' };
  }

  switch (emailStatus) {
    case EmailStatusEnumType.Clicked:
      return { label: 'Clicked through', color: 'green' };
    case EmailStatusEnumType.Bounced:
      return { label: 'Bounced', color: 'red' };
    case EmailStatusEnumType.Opened:
      return { label: 'Opened', color: 'amber' };
    case EmailStatusEnumType.Delivered:
      return { label: 'Delivered', color: 'gray' };
    case EmailStatusEnumType.Sent:
      return { label: 'Sent', color: 'gray' };
    default:
      return { label: 'Not emailed', color: 'gray' };
  }
}

// "Active" is the technical status; to a GP an active invite simply means the investor was invited.
function getInviteStatusBadge(status: string) {
  if (status === 'ACTIVE') return { label: 'Invited', color: 'green' };
  return statusEnumToReadable(status);
}

const DEAL_INVITES_QUERY = gql(`
  query GetActiveInvites($id: ID!, $cursor: ID, $limit: Int, $filters: DealInvitesFilterType) {
    deal(id: $id) {
      id
      invites(cursor: $cursor, limit: $limit, filters: $filters) {
        nodes {
          id
          user {
            id
            name
          }
          status
          emailStatus
          joinedViaLink
          reminderCount
          lastReminderAt
          createdAt
        }
        pageInfo {
          hasNextPage
          cursor
          totalCount
        }
      }
    }
  }
`);

const InvitesTable: FC<{ type: ManageDealInvitesEnumType; searchText: string }> = ({
  type,
  searchText,
}) => {
  const { dealId } = useParams<{ dealId: string }>() as { dealId: string };
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  // Bumping the key remounts the table, which clears its internal row selection.
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  const { loading, error, data, refetch, fetchMore } = useQuery(DEAL_INVITES_QUERY, {
    variables: { id: dealId, limit: PAGE_SIZE, filters: { type, text: searchText } },
  });

  if (loading && !data) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const invites = data.deal.invites.nodes;
  const pageInfo = data.deal.invites.pageInfo;
  // Reminders are offered where the list is made of active invitees; the server skips anyone who
  // already clicked through, is blocked, or has hit the reminder limit.
  const canRemind =
    type === ManageDealInvitesEnumType.Invited || type === ManageDealInvitesEnumType.Opened;
  // "Select all" is resolved on the server as every active invitee, which only matches the "All invited" view.
  const canSelectAllPages = type === ManageDealInvitesEnumType.Invited;
  const recipientCount = selectAll ? pageInfo.totalCount : selectedIds.length;
  const hasSelection = recipientCount > 0;
  const allLoadedRowsSelected = invites.length > 0 && selectedIds.length === invites.length;

  function handleSelect(ids: string[]) {
    setSelectAll(false);
    setSelectedIds(ids);
  }

  function clearSelection() {
    setSelectAll(false);
    setSelectedIds([]);
    setSelectionResetKey(key => key + 1);
  }

  return (
    <>
      {canRemind && (
        <div
          className={classNames(
            'mb-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border p-4 transition-colors',
            hasSelection ? 'border-indigo-200 bg-indigo-50/50' : 'border-gray-200 bg-white'
          )}
        >
          <div className="flex items-center gap-3">
            <span
              className={classNames(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                hasSelection ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600'
              )}
            >
              <BellAlertIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Send a reminder</h3>
              {hasSelection ? (
                <p className="mt-0.5 text-sm text-gray-600">
                  <span className="font-semibold text-indigo-700">{recipientCount}</span> of{' '}
                  {pageInfo.totalCount} investors selected
                  <button
                    type="button"
                    className="ml-3 text-indigo-600 hover:underline"
                    onClick={clearSelection}
                  >
                    Clear
                  </button>
                  {canSelectAllPages &&
                    allLoadedRowsSelected &&
                    pageInfo.hasNextPage &&
                    !selectAll && (
                      <button
                        type="button"
                        className="ml-3 text-indigo-600 hover:underline"
                        onClick={() => setSelectAll(true)}
                      >
                        Select all {pageInfo.totalCount}
                      </button>
                    )}
                </p>
              ) : (
                <p className="mt-0.5 text-sm text-gray-500">
                  Tick investors in the table to remind them. Each can be reminded up to{' '}
                  {MAX_REMINDERS_PER_INVITE} times, at least {REMINDER_COOLDOWN_DAYS} days apart.
                </p>
              )}
            </div>
          </div>
          <SendInviteRemindersDialogButton
            dealId={dealId}
            selectedInviteIds={selectedIds}
            selectAll={selectAll}
            recipientCount={recipientCount}
            disabled={!canRemind}
            onSent={clearSelection}
          />
        </div>
      )}
      <DataTable
        key={selectionResetKey}
        data={invites}
        onSelect={canRemind ? handleSelect : undefined}
        columns={[
          ...(canRemind ? [{ label: 'Select', fieldName: 'id', type: 'SELECT' as const }] : []),
          { label: 'Name', fieldName: 'user.name' },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'CUSTOM',
            customValue: (status: string) => {
              const { label, color } = getInviteStatusBadge(status);
              return <Badge label={label} color={color} showIndicator={true} />;
            },
          },
          {
            label: 'Email Activity',
            // An empty fieldName hands the whole row to customValue.
            fieldName: '',
            type: 'CUSTOM',
            headerNote:
              'Opens are approximate: some email apps load images automatically. Clicked through means the investor followed the link to the deal.',
            customValue: (invite: { emailStatus: EmailStatusEnumType; joinedViaLink: boolean }) => {
              const { label, color } = getEmailActivityBadge(
                invite.emailStatus,
                invite.joinedViaLink
              );
              return <Badge label={label} color={color} />;
            },
          },
          {
            label: 'Reminders',
            fieldName: 'reminderCount',
            type: 'CUSTOM',
            customValue: (count: number) => `${count}/${MAX_REMINDERS_PER_INVITE}`,
          },
          { label: 'Invited On', fieldName: 'createdAt', type: 'DATETIME' },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        paginationLoading={loading}
        onLoadMore={() => fetchMore({ variables: { cursor: pageInfo.cursor } })}
        emptyListTitle="No invites"
        emptyListDescription="Invites matching this filter will appear here."
      />
    </>
  );
};

export default InvitesTable;
